import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';
import DailyLaughGoal from '../components/Gamification/DailyLaughGoal';
import { LaughOMeterWidget, BadgeShowcase } from '../components/Gamification/LaughOMeter';
import { EasterEggTracker } from '../components/Gamification/EasterEggTracker';
import PersonalReportBadges from '../components/Gamification/PersonalReportBadges';

// Import Book Advertisement
import { MediumBookAd, LargeBookAd, BannerBookAd } from '../components/Ads/BookAdvertisements';

const AchievementIcon = ({ icon, size = '2rem' }) => {
  if (icon && icon.startsWith('fa')) {
    return <i className={icon} style={{ fontSize: size, color: '#047857' }} />;
  }
  return <span style={{ fontSize: size }}>{icon}</span>;
};

const AchievementsPage = ({ showToast }) => {
  const { token, user } = useAuth();
  const [achievements, setAchievements] = useState({ earned: [], unearned: [], total_points: 0, level: 1 });
  const [allAchievements, setAllAchievements] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-achievements');
  const [shareModal, setShareModal] = useState(null);
  const [checkingNew, setCheckingNew] = useState(false);

  const fetchData = useCallback(() => {
    let done = 0;
    const total = 3;
    const onDone = () => { done++; if (done >= total) setLoading(false); };
    const safeTimer = setTimeout(() => setLoading(false), 8000);
    
    const makeXHR = (url, callback, headers) => {
      const x = new XMLHttpRequest();
      x.open('GET', url, true);
      if (headers) Object.entries(headers).forEach(([k,v]) => x.setRequestHeader(k, v));
      x.onload = function() {
        if (x.status === 200) { try { callback(JSON.parse(x.responseText)); } catch(e) { callback(null); } }
        else callback(null);
      };
      x.onerror = function() { callback(null); };
      x.send();
    };
    
    makeXHR(`${API}/gamification/achievements`, (data) => {
      if (data) setAllAchievements(data.achievements || []);
      onDone();
    });
    makeXHR(`${API}/gamification/leaderboard/weekly`, (data) => {
      if (data) setWeeklyLeaderboard(data.leaderboard || []);
      onDone();
    });
    makeXHR(`${API}/gamification/leaderboard/all-time`, (data) => {
      if (data) setAllTimeLeaderboard(data.leaderboard || []);
      onDone();
    });
    
    if (token) {
      makeXHR(`${API}/gamification/my-achievements`, (data) => {
        if (data) setAchievements(data);
      }, { Authorization: `Bearer ${token}` });
    }
    
    return () => clearTimeout(safeTimer);
  }, [token]);

  const checkNewAchievements = () => {
    if (!token) return;
    setCheckingNew(true);
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/gamification/check-achievements`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          if (data.new_achievements?.length > 0) {
            data.new_achievements.forEach(a => showToast(`Achievement Unlocked: ${a.icon} ${a.name}!`, 'success'));
            fetchData();
          } else { showToast(data.message || 'Keep going! More achievements await!', 'info'); }
        } catch(e) {}
      }
      setCheckingNew(false);
    };
    x.onerror = function() { setCheckingNew(false); showToast('Failed to check achievements', 'error'); };
    x.send();
  };

  const handleShareAchievement = (achievementId) => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/gamification/share-achievement/${achievementId}`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) { try { setShareModal(JSON.parse(x.responseText)); } catch(e) {} }
    };
    x.onerror = function() { showToast('Failed to generate share link', 'error'); };
    x.send();
  };

  const copyShareText = async () => {
    if (shareModal?.share_message) {
      try {
        await navigator.clipboard.writeText(shareModal.share_message);
        showToast('Copied to clipboard!', 'success');
      } catch (e) {
        showToast('Failed to copy', 'error');
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', color: '#0F172A' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: 20 }}>🏆</div>
          <p>Loading your glorious achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(253, 224, 71, 0.2) 100%)',
        borderRadius: 20,
        padding: 30,
        marginBottom: 30,
        border: '1px solid rgba(245, 158, 11, 0.3)',
        textAlign: 'center'
      }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontWeight: 800, 
          marginBottom: 10,
          background: 'linear-gradient(135deg, #047857 0%, #0F172A 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🏆 Achievement Center
        </h1>
        <p style={{ color: '#a1a1aa', fontSize: '1.1rem', marginBottom: 20 }}>
          Collect badges, climb leaderboards, and prove you&apos;re the ultimate InfoPilot!
        </p>
        
        {/* Laugh-O-Meter and Daily Goal - NEW! */}
        {token && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 20,
            marginBottom: 25,
            textAlign: 'left'
          }}>
            <DailyLaughGoal />
            <LaughOMeterWidget />
          </div>
        )}
        
        {/* User Stats */}
        {token && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 30, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', color: '#f59e0b', fontWeight: 700 }}>{achievements.total_points}</div>
              <div style={{ color: '#a1a1aa' }}>Total Points</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', color: '#FDE047', fontWeight: 700 }}>Lv.{achievements.level}</div>
              <div style={{ color: '#a1a1aa' }}>{achievements.level_name}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', color: '#10b981', fontWeight: 700 }}>{achievements.earned?.length || 0}</div>
              <div style={{ color: '#a1a1aa' }}>Achievements</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', color: '#047857', fontWeight: 700 }}>{achievements.completion_percentage || 0}%</div>
              <div style={{ color: '#a1a1aa' }}>Complete</div>
            </div>
          </div>
        )}
        
        {token && (
          <button 
            onClick={checkNewAchievements}
            disabled={checkingNew}
            style={{
              marginTop: 20,
              padding: '12px 30px',
              background: checkingNew ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #047857 0%, #0F172A 100%)',
              border: 'none',
              borderRadius: 30,
              color: '#0F172A',
              fontWeight: 600,
              cursor: checkingNew ? 'wait' : 'pointer'
            }}
          >
            {checkingNew ? '🔍 Checking...' : '🎯 Check for New Achievements'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 25, flexWrap: 'wrap' }}>
        {['my-achievements', 'all-achievements', 'weekly-leaderboard', 'all-time-leaderboard'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: activeTab === tab 
                ? 'linear-gradient(135deg, #FDE047 0%, #FDE68A 100%)' 
                : 'rgba(255,255,255,0.1)',
              color: '#0F172A',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {tab === 'my-achievements' && '🏅 My Achievements'}
            {tab === 'all-achievements' && '📋 All Achievements'}
            {tab === 'weekly-leaderboard' && '📊 Weekly Top'}
            {tab === 'all-time-leaderboard' && '🌟 All-Time Legends'}
          </button>
        ))}
      </div>

      {/* My Achievements Tab */}
      {activeTab === 'my-achievements' && (
        <div>
          {!token ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#a1a1aa' }}>
              <div style={{ fontSize: '3rem', marginBottom: 15 }}>🔐</div>
              <p>Log in to track your achievements!</p>
            </div>
          ) : (
            <>
              <h2 style={{ color: '#10b981', marginBottom: 20 }}>✅ Earned ({achievements.earned?.length || 0})</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 15, marginBottom: 30 }}>
                {achievements.earned?.map((achievement, i) => (
                  <div key={i} style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(253, 224, 71, 0.1))',
                    borderRadius: 15,
                    padding: 20,
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 10 }}>
                      <AchievementIcon icon={achievement.icon} />
                      <div>
                        <div style={{ color: '#0F172A', fontWeight: 600 }}>{achievement.name}</div>
                        <div style={{ color: '#10b981', fontSize: '0.8rem' }}>+{achievement.points} pts</div>
                      </div>
                    </div>
                    <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: 10 }}>{achievement.description}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#71717a', fontSize: '0.75rem' }}>
                        Earned: {new Date(achievement.earned_at).toLocaleDateString()}
                      </span>
                      <button 
                        onClick={() => handleShareAchievement(achievement.id)}
                        style={{
                          padding: '5px 12px',
                          background: 'rgba(255,255,255,0.1)',
                          border: 'none',
                          borderRadius: 15,
                          color: '#FDE047',
                          fontSize: '0.8rem',
                          cursor: 'pointer'
                        }}
                      >
                        📤 Share
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <h2 style={{ color: '#f59e0b', marginBottom: 20 }}>🎯 Not Yet Earned ({achievements.unearned?.length || 0})</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 15 }}>
                {achievements.unearned?.map((achievement, i) => (
                  <div key={i} style={{
                    background: '#475569',
                    borderRadius: 15,
                    padding: 20,
                    border: '1px solid rgba(255,255,255,0.1)',
                    opacity: 0.7
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 10 }}>
                      <span style={{ filter: 'grayscale(100%)' }}><AchievementIcon icon={achievement.icon} /></span>
                      <div>
                        <div style={{ color: '#0F172A', fontWeight: 600 }}>{achievement.name}</div>
                        <div style={{ color: '#f59e0b', fontSize: '0.8rem' }}>+{achievement.points} pts</div>
                      </div>
                    </div>
                    <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>{achievement.description}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* All Achievements Tab */}
      {activeTab === 'all-achievements' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 15 }}>
          {allAchievements.map((achievement, i) => (
            <div key={i} style={{
              background: '#334155',
              borderRadius: 15,
              padding: 20,
              border: '1px solid rgba(253, 224, 71, 0.2)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginBottom: 10 }}>
                <AchievementIcon icon={achievement.icon} />
                <div>
                  <div style={{ color: '#0F172A', fontWeight: 600 }}>{achievement.name}</div>
                  <div style={{ color: '#FDE047', fontSize: '0.8rem' }}>+{achievement.points} pts • {achievement.category}</div>
                </div>
              </div>
              <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>{achievement.description}</p>
            </div>
          ))}
        </div>
      )}

      {/* Weekly Leaderboard Tab */}
      {activeTab === 'weekly-leaderboard' && (
        <div style={{
          background: '#334155',
          borderRadius: 20,
          padding: 25,
          border: '1px solid rgba(245, 158, 11, 0.2)'
        }}>
          <h2 style={{ color: '#f59e0b', marginBottom: 20 }}>📊 This Week&apos;s Top Sellers</h2>
          {weeklyLeaderboard.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#a1a1aa' }}>
              <div style={{ fontSize: '3rem', marginBottom: 15 }}>🏆</div>
              <p>No sales yet this week. Be the first to claim the crown!</p>
            </div>
          ) : (
            <div>
              {weeklyLeaderboard.map((entry, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  padding: 15,
                  background: i === 0 ? 'rgba(253, 230, 138, 0.1)' : 'transparent',
                  borderRadius: 10,
                  marginBottom: 10,
                  border: i === 0 ? '1px solid rgba(253, 230, 138, 0.3)' : 'none'
                }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: i === 0 ? '#ffd700' : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'rgba(255,255,255,0.1)',
                    color: i < 3 ? '#000' : '#fff',
                    fontWeight: 700
                  }}>
                    {entry.rank}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#0F172A', fontWeight: 600 }}>{entry.username}</div>
                    <div style={{ color: '#a1a1aa', fontSize: '0.8rem' }}>
                      {entry.weekly_sales} sales • ${entry.weekly_revenue?.toFixed(2)} earned
                    </div>
                  </div>
                  {i === 0 && <span style={{ fontSize: '1.5rem' }}>👑</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All-Time Leaderboard Tab */}
      {activeTab === 'all-time-leaderboard' && (
        <div style={{
          background: '#334155',
          borderRadius: 20,
          padding: 25,
          border: '1px solid rgba(253, 224, 71, 0.2)'
        }}>
          <h2 style={{ color: '#FDE047', marginBottom: 20 }}>🌟 All-Time Achievement Legends</h2>
          {allTimeLeaderboard.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#a1a1aa' }}>
              <div style={{ fontSize: '3rem', marginBottom: 15 }}>🏆</div>
              <p>Start earning achievements to climb the leaderboard!</p>
            </div>
          ) : (
            <div>
              {allTimeLeaderboard.map((entry, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 20,
                  padding: 15,
                  background: i === 0 ? 'rgba(253, 224, 71, 0.2)' : 'transparent',
                  borderRadius: 10,
                  marginBottom: 10,
                  border: i === 0 ? '1px solid rgba(253, 224, 71, 0.3)' : 'none'
                }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: i === 0 ? 'linear-gradient(135deg, #FDE047, #FDE68A)' : 'rgba(255,255,255,0.1)',
                    color: '#0F172A',
                    fontWeight: 700
                  }}>
                    {entry.rank}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#0F172A', fontWeight: 600 }}>{entry.username}</div>
                    <div style={{ color: '#a1a1aa', fontSize: '0.8rem' }}>
                      {entry.total_points} points • {entry.achievement_count} achievements • {entry.level_name}
                    </div>
                  </div>
                  {i === 0 && <span style={{ fontSize: '1.5rem' }}>🏆</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Share Modal */}
      {shareModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShareModal(null)}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: 20,
            padding: 30,
            maxWidth: 500,
            width: '90%',
            border: '1px solid rgba(253, 224, 71, 0.3)'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#0F172A', marginBottom: 20 }}>📤 Share Your Achievement!</h3>
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              padding: 15,
              borderRadius: 10,
              marginBottom: 20
            }}>
              <p style={{ color: '#e0e0e0', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
                {shareModal.share_message}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button 
                onClick={copyShareText}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #FDE047, #FDE68A)',
                  border: 'none',
                  borderRadius: 10,
                  color: '#0F172A',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                📋 Copy Text
              </button>
              <a 
                href={shareModal.platforms?.twitter}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  background: '#1da1f2',
                  border: 'none',
                  borderRadius: 10,
                  color: '#0F172A',
                  fontWeight: 600,
                  textDecoration: 'none',
                  textAlign: 'center'
                }}
              >
                🐦 Twitter
              </a>
              <button 
                onClick={() => setShareModal(null)}
                style={{
                  padding: '12px 20px',
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: 10,
                  color: '#0F172A',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book Promo */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(253, 230, 138, 0.2) 0%, rgba(253, 224, 71, 0.2) 100%)',
        borderRadius: 20,
        padding: 30,
        marginTop: 30,
        border: '1px solid rgba(253, 230, 138, 0.3)',
        textAlign: 'center'
      }}>
        <h3 style={{ color: '#047857', marginBottom: 15, fontSize: '1.3rem' }}>
          📚 Achievement Unlocked: Book Reader!
        </h3>
        <p style={{ color: '#0F172A', fontSize: '1rem', marginBottom: 15, maxWidth: 600, margin: '0 auto 15px' }}>
          Want the ultimate achievement? Read &quot;Letters to Evelyn&quot; - a supernatural thriller comedy 
          so good, even the ghosts wrote 5-star reviews! (Well, they would have if they had Amazon accounts...)
        </p>
        <button 
          onClick={() => window.open('https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US', '_blank')}
          style={{
            background: 'linear-gradient(135deg, #FDE68A 0%, #FDE047 100%)',
            color: '#0F172A',
            border: 'none',
            padding: '15px 40px',
            borderRadius: 30,
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          📖 Unlock the Book Achievement - Only $2.99!
        </button>
      </div>
      
      {/* Easter Egg Tracker */}
      {token && <EasterEggTracker />}
      
      {/* Personal Report Writing Badges */}
      {token && (
        <div style={{ marginTop: 20 }}>
          <PersonalReportBadges 
            userStats={achievements.personal_report_stats || {}}
            earnedBadges={achievements.earned?.filter(b => b.category === 'personal_reports') || []}
          />
        </div>
      )}
      
      {/* Book Advertisements */}
      <LargeBookAd variant={2} />
      <BannerBookAd variant={3} />
    </div>
  );
};

export default AchievementsPage;
